import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { accountApi } from "@/features/account/api";
import { accountKeys } from "@/features/account/query-keys";
import type { ChangePasswordRequest } from "@/features/account/types";
import { useAuthStore } from "@/stores/authStore";

export function useAccountQuery() {
  return useQuery({
    queryKey: accountKeys.me(),
    queryFn: accountApi.me,
  });
}

/** onError is a no-op here: these forms show the error inline (ErrorAlert) next to the
 * field, so the global toast (see queryClient's default mutation onError) would be redundant. */
export function useUpdateEmailMutation() {
  const queryClient = useQueryClient();
  const setAccountAndEmail = useAuthStore((s) => s.setAccountAndEmail);
  return useMutation({
    mutationFn: accountApi.updateEmail,
    onSuccess: (account) => {
      setAccountAndEmail(account);
      queryClient.setQueryData(accountKeys.me(), account);
      toast.success(i18n.t("account.toast.emailUpdated"));
    },
    onError: () => {},
  });
}

export function useChangePasswordMutation() {
  const setPassword = useAuthStore((s) => s.setPassword);
  return useMutation({
    mutationFn: (req: ChangePasswordRequest) => accountApi.changePassword(req),
    onSuccess: (_, variables) => {
      setPassword(variables.newPassword);
      toast.success(i18n.t("account.toast.passwordUpdated"));
    },
    onError: () => {},
  });
}

export function useDeleteAccountMutation() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({
    mutationFn: accountApi.deleteAccount,
    onSuccess: () => {
      logout();
    },
    onError: () => {},
  });
}
